// Pro on the web through Stripe (contracts/web.md §5): modes, plans, currencies and money in minor units, the
// key checks the panel runs before it sends anything, and plain copy for the payments error codes. Shared by
// Monetisation → Web payments, the player detail and the mock (src/mocks/payments.ts).

import { ApiError } from "../api/client";

// ---------------------------------------------------------------- modes and plans

export const PAYMENT_MODES = ["test", "live"] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];
export const MODE_LABEL: Record<PaymentMode, string> = { test: "Test", live: "Live" };

export const WEB_PLANS = ["monthly", "annual", "lifetime"] as const;
export type WebPlan = (typeof WEB_PLANS)[number];
export const WEB_PLAN_LABEL: Record<WebPlan, string> = { monthly: "Monthly", annual: "Annual", lifetime: "Lifetime" };
/** The Stripe price each plan gets (§5.1: interval is derived from the plan). */
export const PLAN_INTERVAL: Record<WebPlan, string> = { monthly: "per month", annual: "per year", lifetime: "one-time" };

/** The three iOS-matching packages "Create default packages" makes (§5.3). */
export const DEFAULT_PACKAGES: { plan: WebPlan; name: string; amount: number; currency: string; trialDays: number; badge: string }[] = [
  { plan: "monthly", name: "Monthly", amount: 599, currency: "usd", trialDays: 0, badge: "" },
  { plan: "annual", name: "Annual", amount: 2999, currency: "usd", trialDays: 7, badge: "Best value" },
  { plan: "lifetime", name: "Lifetime", amount: 9999, currency: "usd", trialDays: 0, badge: "" },
];

/** Admin-side limits (Stripe's own maximum for unit_amount is 99,999,999 minor units). */
export const LIMITS = { name: 60, description: 300, badge: 24, trialDays: 365, graceDays: 30, maxAmount: 99_999_999, extraCurrencies: 10 } as const;

// ---------------------------------------------------------------- currencies

/** Stripe's zero-decimal currencies: the amount is sent as is (¥800 → 800). */
export const ZERO_DECIMAL = new Set(["bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga", "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf"]);
/** Stripe's three-decimal currencies. */
export const THREE_DECIMAL = new Set(["bhd", "jod", "kwd", "omr", "tnd"]);

/** Digits after the decimal point in the currency's major unit. */
export const currencyExponent = (currency: string) => {
  const c = currency.toLowerCase();
  return ZERO_DECIMAL.has(c) ? 0 : THREE_DECIMAL.has(c) ? 3 : 2;
};

/** The currencies the package form offers (Stripe supports more; the API takes any it supports). */
export const CURRENCIES: { code: string; name: string }[] = [
  { code: "usd", name: "US dollar" },
  { code: "eur", name: "Euro" },
  { code: "gbp", name: "British pound" },
  { code: "cad", name: "Canadian dollar" },
  { code: "aud", name: "Australian dollar" },
  { code: "nzd", name: "New Zealand dollar" },
  { code: "chf", name: "Swiss franc" },
  { code: "sek", name: "Swedish krona" },
  { code: "nok", name: "Norwegian krone" },
  { code: "dkk", name: "Danish krone" },
  { code: "pln", name: "Polish złoty" },
  { code: "czk", name: "Czech koruna" },
  { code: "try", name: "Turkish lira" },
  { code: "brl", name: "Brazilian real" },
  { code: "mxn", name: "Mexican peso" },
  { code: "clp", name: "Chilean peso" },
  { code: "inr", name: "Indian rupee" },
  { code: "jpy", name: "Japanese yen" },
  { code: "krw", name: "South Korean won" },
  { code: "sgd", name: "Singapore dollar" },
  { code: "hkd", name: "Hong Kong dollar" },
  { code: "thb", name: "Thai baht" },
  { code: "idr", name: "Indonesian rupiah" },
  { code: "php", name: "Philippine peso" },
  { code: "myr", name: "Malaysian ringgit" },
  { code: "vnd", name: "Vietnamese dong" },
  { code: "zar", name: "South African rand" },
  { code: "aed", name: "UAE dirham" },
  { code: "sar", name: "Saudi riyal" },
  { code: "ils", name: "Israeli new shekel" },
];
const CURRENCY_NAME = new Map(CURRENCIES.map((c) => [c.code, c.name]));
export const currencyName = (code: string) => CURRENCY_NAME.get(code.toLowerCase()) ?? code.toUpperCase();

const MAJOR = /^\d+(\.\d+)?$/;

/** Why a major-unit amount ("5.99", "800") can't be sent for this currency, or null. */
export function amountProblem(major: string, currency: string): string | null {
  const v = major.trim();
  if (!v) return "Enter a price.";
  if (!MAJOR.test(v)) return "Use digits and a dot, e.g. 5.99.";
  const exp = currencyExponent(currency);
  const decimals = v.split(".")[1]?.length ?? 0;
  if (decimals > exp) return exp === 0 ? `${currency.toUpperCase()} has no decimals.` : `${currency.toUpperCase()} takes at most ${exp} decimals.`;
  const minor = toMinor(v, currency);
  if (minor === null || minor <= 0) return "The price must be more than 0.";
  if (minor > LIMITS.maxAmount) return "That's above Stripe's maximum.";
  return null;
}

/**
 * A major-unit amount as Stripe's minor units, without floating point: "5.99" USD → 599, "800" JPY → 800,
 * "1.5" KWD → 1500. Null when it isn't a plain number or has more decimals than the currency.
 */
export function toMinor(major: string, currency: string): number | null {
  const v = major.trim();
  if (!MAJOR.test(v)) return null;
  const exp = currencyExponent(currency);
  const [whole = "0", frac = ""] = v.split(".");
  if (frac.length > exp) return null;
  return Number(whole) * 10 ** exp + (exp ? Number(frac.padEnd(exp, "0")) : 0);
}

/** Minor units back to the form's major-unit text: 599 USD → "5.99", 800 JPY → "800". */
export function fromMinor(minor: number, currency: string): string {
  const exp = currencyExponent(currency);
  if (exp === 0) return String(minor);
  const s = String(Math.abs(minor)).padStart(exp + 1, "0");
  return `${minor < 0 ? "-" : ""}${s.slice(0, -exp)}.${s.slice(-exp)}`;
}

/** 2999, "usd" → "$29.99"; 800, "jpy" → "¥800" (Intl, in the currency's own decimals). */
export function formatMoney(minor: number | null | undefined, currency: string | null | undefined, locale = "en-US"): string {
  if (minor === null || minor === undefined || !currency) return "—";
  const exp = currencyExponent(currency);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: exp,
      maximumFractionDigits: exp,
    }).format(minor / 10 ** exp);
  } catch {
    return `${fromMinor(minor, currency)} ${currency.toUpperCase()}`;
  }
}

// ---------------------------------------------------------------- keys

const SECRET = /^(sk|rk)_(test|live)_[A-Za-z0-9]{8,}$/;
const PUBLISHABLE = /^pk_(test|live)_[A-Za-z0-9]{8,}$/;

/** Why a pasted secret (sk_…) or restricted (rk_…) key can't be used for this mode, or null. */
export function secretKeyProblem(mode: PaymentMode, key: string): string | null {
  const k = key.trim();
  if (!k) return "Paste the secret key.";
  if (k.startsWith("pk_")) return "That's the publishable key. The secret key starts with sk_ or rk_.";
  const m = SECRET.exec(k);
  if (!m) return `Expected sk_${mode}_… or rk_${mode}_….`;
  if (m[2] !== mode) return `That's a ${m[2]} key. ${MODE_LABEL[mode]} mode needs sk_${mode}_… or rk_${mode}_….`;
  return null;
}

/** Why a pasted publishable key (pk_…) can't be used for this mode, or null. */
export function publishableKeyProblem(mode: PaymentMode, key: string): string | null {
  const k = key.trim();
  if (!k) return "Paste the publishable key.";
  if (/^(sk|rk)_/.test(k)) return "That's a secret key. The publishable key starts with pk_.";
  const m = PUBLISHABLE.exec(k);
  if (!m) return `Expected pk_${mode}_….`;
  if (m[1] !== mode) return `That's a ${m[1]} key. ${MODE_LABEL[mode]} mode needs pk_${mode}_….`;
  return null;
}

/** What a restricted key (rk_…) needs (§5.2). */
export const RESTRICTED_KEY_PERMISSIONS: { resource: string; access: "Write" | "Read" }[] = [
  { resource: "Products", access: "Write" },
  { resource: "Prices", access: "Write" },
  { resource: "Customers", access: "Write" },
  { resource: "Checkout Sessions", access: "Write" },
  { resource: "Customer portal", access: "Write" },
  { resource: "Subscriptions", access: "Write" },
  { resource: "Webhook endpoints", access: "Write" },
  { resource: "Charges", access: "Read" },
  { resource: "Invoices", access: "Read" },
  { resource: "Payment intents", access: "Read" },
];

/** The webhook events the server subscribes the endpoint to (§5.2). */
export const WEBHOOK_EVENTS = [
  "checkout.session.completed",
  "checkout.session.async_payment_succeeded",
  "customer.subscription.created",
  "customer.subscription.updated",
  "customer.subscription.deleted",
  "invoice.paid",
  "invoice.payment_failed",
  "charge.refunded",
  "charge.dispute.created",
  "charge.dispute.closed",
] as const;

/** Stripe dashboard links, for rows the API sends without one. */
export function stripeDashboardUrl(mode: PaymentMode, path: string): string {
  return `https://dashboard.stripe.com/${mode === "test" ? "test/" : ""}${path.replace(/^\//, "")}`;
}

// ---------------------------------------------------------------- errors

const PACKAGE_CONFLICT = "This plan already has an active package in this mode. Archive that one first, or save this one as inactive.";

/**
 * Plain copy for the payments error codes (§5.7); null when the code has none (the server's message is shown).
 * On the package routes any other 409 reads as the one-active-package-per-plan rule.
 */
export function paymentsErrorCopy(error: unknown, context: "keys" | "settings" | "package" = "keys"): string | null {
  if (!(error instanceof ApiError)) return null;
  if (context === "package" && error.status === 409 && error.code !== "stripe_not_connected") return PACKAGE_CONFLICT;
  switch (error.code) {
    case "stripe_key_invalid":
      return "Stripe didn't accept this key. Copy it again from Developers → API keys, the whole key.";
    case "stripe_mode_mismatch":
      return "This key is for the other mode. Test mode takes sk_test_ and pk_test_ keys; live mode takes sk_live_ and pk_live_.";
    case "stripe_unreachable":
      return "Couldn't reach Stripe. Nothing was saved; try again in a minute.";
    case "stripe_not_connected":
      return "That mode isn't connected. Connect its keys first.";
    case "package_conflict":
      return PACKAGE_CONFLICT;
    case "forbidden":
      return "You need the admin role for this.";
    case "network":
      return "Can't reach the admin API.";
    default:
      return error.status === 409 ? "That clashes with the current state. Reload and try again." : null;
  }
}
